import { useCallback, useEffect, useRef, useState } from 'react';
import { aracBilgiAPI } from '../../api/api';
import { ARAC_GRUPLARI, katla, paketUyar, tsbMarkaBul, tsbModelBul, tutarYaz } from '../../utils/aracHasar';

/*
  TSB Kasko Değer Listesi ile araç seçimi: model yılı → marka → paket ve
  aracın kasko değeri (kaza ayının listesi + güncel liste).

  Liste sunucu üzerinden TSB'den gelir. TSB'ye ulaşılamazsa ya da araç
  listede yoksa (15 model yılından eski) ekran elle girişe düşer.

  Formda saklananlar: modelYili, marka, model (adlar), tsbMarkaId,
  tsbModelId. Adlar da saklandığı için yıl değişince aynı marka ve paket
  yeni yılın listesinde adıyla yeniden bulunur.
*/
export default function useTsb(f, setF) {
  const [yillar, setYillar] = useState([]);
  const [listeYok, setListeYok] = useState(false);
  const [markalar, setMarkalar] = useState([]);
  const [modeller, setModeller] = useState([]);
  const [yukleniyor, setYukleniyor] = useState('');
  const [deger, setDeger] = useState(null);
  const [paketFiltre, setPaketFiltre] = useState('');
  // Ruhsatta marka okunamadıysa okunan model adı: marka listeden seçilince paketler buna göre süzülür.
  const ruhsatModelIpucu = useRef('');

  const yil = Number(f.modelYili) || 0;
  const yilListede = yillar.includes(yil);
  const markaId = Number(f.tsbMarkaId) || 0;
  const modelId = Number(f.tsbModelId) || 0;
  // Elle giriş: kullanıcı seçtiyse, TSB'ye ulaşılamadıysa ya da yıl listede yoksa (15 yıldan eski).
  const elle = !!f.elleArac || listeYok || (yillar.length > 0 && !!f.modelYili && !yilListede);

  // 1) Model yılları — bir kez.
  useEffect(() => {
    let bitti = false;
    aracBilgiAPI.tsbYillar()
      .then((l) => {
        if (bitti) return;
        if (Array.isArray(l) && l.length) setYillar(l.map(Number));
        else setListeYok(true);
      })
      .catch(() => { if (!bitti) setListeYok(true); });
    return () => { bitti = true; };
  }, []);

  // 2) Yıl seçilince markalar. Önceki marka adıyla yeniden bulunur.
  useEffect(() => {
    if (elle || !yilListede) { setMarkalar([]); return undefined; }
    let bitti = false;
    setYukleniyor('marka');
    aracBilgiAPI.tsbMarkalar(yil)
      .then((l) => {
        if (bitti) return;
        const liste = Array.isArray(l) ? l : [];
        setMarkalar(liste);
        setF((o) => {
          if (liste.some((m) => String(m.id) === String(o.tsbMarkaId))) return o;
          const m = o.marka ? tsbMarkaBul(liste, o.marka) : null;
          return { ...o, tsbMarkaId: m ? String(m.id) : '', ...(m ? {} : { tsbModelId: '' }) };
        });
      })
      .catch(() => { if (!bitti) setMarkalar([]); })
      .finally(() => { if (!bitti) setYukleniyor(''); });
    return () => { bitti = true; };
  }, [yil, yilListede, elle, setF]);

  // 3) Marka seçilince paketler. Önceki paket adıyla yeniden bulunur.
  useEffect(() => {
    if (elle || !yilListede || !markaId) { setModeller([]); return undefined; }
    let bitti = false;
    setYukleniyor('model');
    aracBilgiAPI.tsbModeller(yil, markaId)
      .then((l) => {
        if (bitti) return;
        const liste = Array.isArray(l) ? l : [];
        setModeller(liste);
        setF((o) => {
          if (liste.some((m) => String(m.id) === String(o.tsbModelId))) return o;
          const m = o.model ? liste.find((x) => katla(x.ad) === katla(o.model)) : null;
          return { ...o, tsbModelId: m ? String(m.id) : '' };
        });
        // Marka ruhsattan okunamamış ve elle seçilmişse paketler ruhsattaki modele göre süzülür.
        const ipucu = ruhsatModelIpucu.current;
        if (ipucu) {
          ruhsatModelIpucu.current = '';
          const ilk = katla(ipucu).split(' ')[0];
          if (ilk && liste.some((p) => paketUyar(p.ad, ilk))) setPaketFiltre(ilk);
        }
      })
      .catch(() => { if (!bitti) setModeller([]); })
      .finally(() => { if (!bitti) setYukleniyor(''); });
    return () => { bitti = true; };
  }, [yil, yilListede, markaId, elle, setF]);

  // 4) Paket ya da kaza tarihi değişince kasko değeri. Kaza ayının değeri
  //    6. bölümdeki TSB kutusuna yazılır (kullanıcı kendisi yazmadıysa).
  useEffect(() => {
    if (elle || !yilListede || !modelId) { setDeger(null); return undefined; }
    let bitti = false;
    setYukleniyor('deger');
    aracBilgiAPI.tsbDeger(yil, modelId, f.kazaTarihi)
      .then((d) => {
        if (bitti) return;
        if (!d || d.bulunamadi) { setDeger(null); return; }
        setDeger(d);
        const esas = d.kazaAyi?.tutar || d.guncel?.tutar;
        if (esas) setF((o) => (o.tsb && !o.tsbOtomatik ? o : { ...o, tsb: tutarYaz(esas), tsbOtomatik: true }));
      })
      .catch(() => { if (!bitti) setDeger(null); })
      .finally(() => { if (!bitti) setYukleniyor(''); });
    return () => { bitti = true; };
  }, [yil, yilListede, modelId, f.kazaTarihi, elle, setF]);

  /* ---------------- Seçimler ---------------- */
  const yilSec = (v) => {
    if (v === 'eski') { setF((o) => ({ ...o, elleArac: true, modelYili: '', tsbMarkaId: '', tsbModelId: '' })); return; }
    setF((o) => ({ ...o, modelYili: v }));
  };
  const markaSec = (id) => {
    const m = markalar.find((x) => String(x.id) === String(id));
    setPaketFiltre('');
    setF((o) => ({ ...o, tsbMarkaId: id, marka: m ? m.ad : '', tsbModelId: '', model: '' }));
  };
  const modelSec = (id) => {
    const m = modeller.find((x) => String(x.id) === String(id));
    setF((o) => ({ ...o, tsbModelId: id, model: m ? m.ad : '' }));
  };
  const elleYaz = (acik) => setF((o) => ({ ...o, elleArac: acik, ...(acik ? {} : { tsbMarkaId: '', tsbModelId: '' }) }));

  // Süzgeç hiçbir pakete uymuyorsa liste boş kalmasın: bütün paketler gösterilir.
  const suzulmus = paketFiltre.trim()
    ? modeller.filter((m) => paketUyar(m.ad, paketFiltre) || String(m.id) === String(f.tsbModelId))
    : modeller;
  const suzgecUymadi = paketFiltre.trim() !== '' && modeller.length > 0 && suzulmus.length === 0;
  const gorunenModeller = suzgecUymadi ? modeller : suzulmus;

  /**
   * Ruhsattan okunan bilgiyi forma işler: yıl, marka, paket (TSB'de
   * bulunursa listeden seçili gelir), cinsi, kullanım, yakıt; plaka
   * yalnızca boşsa. İşlenenlerin kısa özetini döndürür.
   */
  const ruhsattanUygula = useCallback(async (b) => {
    if (!b) return { ozet: '', eksik: '' };
    const y = Number(b.modelYili) || 0;
    let marka = b.marka || '';
    let ticari = b.ticariAdi || '';
    let model = ticari;
    let tsbMarkaId = '';
    let tsbModelId = '';
    let eksik = '';
    let markaListedeYok = false;

    if (y && yillar.includes(y) && (marka || ticari || b.tipi)) {
      try {
        const ml = await aracBilgiAPI.tsbMarkalar(y);
        let m = marka ? tsbMarkaBul(ml, marka) : null;
        if (!m) {
          // Okumada "Markası" satırına model düşebiliyor ("COROLLA"): marka diğer alanlarda aranır.
          for (const aday of [ticari, b.tipi]) {
            const bulunan = aday ? tsbMarkaBul(ml, aday) : null;
            if (!bulunan) continue;
            m = bulunan;
            if (aday === ticari) {
              // "TOYOTA COROLLA" → "COROLLA"; ticari adda yalnız marka varsa model, marka satırındakidir.
              const kalan = katla(aday) === katla(bulunan.ad) ? '' : String(aday).slice(String(bulunan.ad).length).trim();
              ticari = kalan || marka;
            }
            break;
          }
        }
        if (m) {
          tsbMarkaId = String(m.id);
          marka = m.ad;
          // Ekspertiz raporunda tip markayla birlikte yazılabiliyor ("HYUNDAI i20 1.4 MPI …"):
          // marka baştan atılır, paket aramasında yalnız model adı kalır.
          if (katla(ticari).startsWith(`${katla(m.ad)} `)) ticari = String(ticari).slice(String(m.ad).length).trim();
          model = ticari;
          const pl = await aracBilgiAPI.tsbModeller(y, m.id);
          const s = tsbModelBul(pl, ticari);
          if (s.model) {
            tsbModelId = String(s.model.id);
            model = s.model.ad;
          } else {
            // Ruhsatta çoğu zaman yalnız model adı yazar ("ELANTRA"); paket listeden seçilir.
            // Liste, ruhsattaki adın ilk kelimesiyle daraltılır — ama yalnızca bu kelime
            // en az bir pakete uyuyorsa ve markanın kendisi değilse (ör. "TOGG").
            model = '';
            const ilk = katla(ticari).split(' ')[0];
            const uygun = ilk && ilk !== katla(marka) && (pl || []).some((p) => paketUyar(p.ad, ilk));
            setPaketFiltre(uygun ? ilk : '');
            eksik = ticari
              ? `Paketi listeden seçin (ruhsatta “${ticari}” yazıyor).`
              : 'Paketi listeden seçin.';
          }
        } else {
          // Marka listede bulunamadı: alanlar elle yazıma dönmesin (sonradan listeden
          // düzeltilemiyordu). Marka listeden seçilince paketler ruhsattaki modele göre süzülür.
          markaListedeYok = true;
          ruhsatModelIpucu.current = ticari || marka;
          eksik = `Markayı listeden seçin (ruhsatta marka yerine “${marka || ticari}” okundu).`;
        }
      } catch (e) { /* TSB'ye ulaşılamadı; elle yazılmış gibi işlenir */ }
    }

    const elleArac = !tsbMarkaId && !markaListedeYok && !!(marka || model);
    setF((o) => ({
      ...o,
      ...(y ? { modelYili: String(y) } : {}),
      ...(markaListedeYok ? { marka: '' } : marka ? { marka } : {}),
      model: markaListedeYok ? '' : (model || (tsbMarkaId ? '' : o.model)),
      tsbMarkaId,
      tsbModelId,
      elleArac,
      ...(b.grup ? { grup: b.grup } : {}),
      ...(b.kullanim === 'hususi' || b.kullanim === 'ticari' ? { kullanim: b.kullanim } : {}),
      ...(b.yakit ? { yakit: b.yakit } : {}),
      plaka: o.plaka || b.plaka || '',
    }));

    const grupAdi = (ARAC_GRUPLARI.find((g) => g.id === b.grup) || {}).ad || '';
    const parcalar = [markaListedeYok ? '' : marka, markaListedeYok ? '' : model, y || '', grupAdi, b.kullanim === 'hususi' ? 'Hususi' : b.kullanim === 'ticari' ? 'Ticari' : '',
      { benzin: 'Benzin', dizel: 'Dizel', lpg: 'Benzin + LPG', hibrit: 'Hibrit', elektrik: 'Elektrikli' }[b.yakit] || '']
      .filter(Boolean);
    return { ozet: parcalar.join(' · '), eksik };
  }, [yillar, setF]);

  return {
    yillar, listeYok, markalar, modeller: gorunenModeller, tumModelSayisi: modeller.length, suzgecUymadi,
    yukleniyor, deger, paketFiltre, setPaketFiltre, elle, yilListede,
    yilSec, markaSec, modelSec, elleYaz, ruhsattanUygula,
  };
}
